import TagManager from 'react-gtm-module';

import { PreQualificationStep, setPreQualificationStep, setSignInStep, SignInStep } from 'prequalificationV2/actions';
import { clearApplication, setApplication } from 'prequalificationV2/actions/application';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { removeUrlMarketingParamsFromCookie } from 'prequalificationV2/routes/routes.utils';
import {
  PrequalificationNextStep,
  UnderwritingResult,
} from 'prequalificationV2/api/PreQualification/preQualificationApi.enums';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { RunApplicationUnderwritingResponseDto } from 'prequalificationV2/api/PreQualification/preQualificationApi.types';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import { NextStepDestination, resolveNextStepNavigation } from 'prequalificationV2/utils/nextStepNavigation';
import { GOOGLE_TAG_MANAGER_ARGS } from 'constants/constants';
import useAppNavigate from 'hooks/useAppNavigate';

export const useNavigateAfterUnderwriting = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const { borrowerId } = useAppSelector((state) => state.borrower);

  const navigateToNextStep = async (nextStep: PrequalificationNextStep | null) => {
    const navigation = resolveNextStepNavigation(nextStep);

    switch (navigation.destination) {
      case NextStepDestination.Prequalification: {
        // The offers steps need the dashboard/offer data hydrated before they render.
        if (nextStep === PrequalificationNextStep.HilOffers || nextStep === PrequalificationNextStep.PosOffers) {
          await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
        }

        dispatch(setPreQualificationStep(navigation.prequalificationStep!));
        break;
      }
      case NextStepDestination.CreateAccount: {
        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
        dispatch(setSignInStep(SignInStep.CreateAccount));
        navigator(AppRoutes.Login);
        break;
      }
      default: {
        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
        navigator(AppRoutes.Dashboard);
      }
    }
  };

  const getAndNavigateToNextStep = async () => {
    const { step } = await preQualificationApi.getNextStep();

    await navigateToNextStep(step);
  };

  const handleUnderwritingResult = async (
    runApplicationUnderwritingResponseDto: RunApplicationUnderwritingResponseDto,
  ) => {
    dispatch(
      setApplication({
        applicationId: runApplicationUnderwritingResponseDto.applicationId,
        productName: runApplicationUnderwritingResponseDto.productName,
      }),
    );

    if (runApplicationUnderwritingResponseDto.result === UnderwritingResult.Decline) {
      TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationDecline);
    }

    if (
      runApplicationUnderwritingResponseDto.result === UnderwritingResult.Skip ||
      runApplicationUnderwritingResponseDto.result === UnderwritingResult.Success
    ) {
      TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationPreApproved);
      removeUrlMarketingParamsFromCookie();
    }

    switch (runApplicationUnderwritingResponseDto.result) {
      case UnderwritingResult.Decline: {
        dispatch(clearApplication());
        dispatch(setPreQualificationStep(PreQualificationStep.NoOptions));
        break;
      }
      case UnderwritingResult.FrozenReport: {
        dispatch(setPreQualificationStep(PreQualificationStep.UnfreezeCredit));
        break;
      }
      case UnderwritingResult.AddCoBorrower: {
        dispatch(setPreQualificationStep(PreQualificationStep.PromptToAddCoBorrower));
        break;
      }
      case UnderwritingResult.CoBorrowerHasActiveApplication:
      case UnderwritingResult.CoBorrowerLocked: {
        dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerHasApplication));
        break;
      }
      case UnderwritingResult.Skip:
      case UnderwritingResult.Success: {
        await getAndNavigateToNextStep();
        break;
      }
      default: {
        dispatch(setPreQualificationStep(PreQualificationStep.NotFoundContact));
      }
    }
  };

  return {
    navigateToNextStep: navigateToNextStep,
    getAndNavigateToNextStep: getAndNavigateToNextStep,
    handleUnderwritingResult: handleUnderwritingResult,
  };
};
